import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import CivicModal from './CivicModal';
import type { CivicLevel, Badge } from '../lib/civicLevelTypes';
import {
  calculateLevelProgress,
  getNextLevel,
  LEVEL_THRESHOLDS,
  getAllBadges,
} from '../lib/civicLevelService';

interface CivicLevelCardProps {
  civicLevel: CivicLevel;
  xp: number;
  userId: string;
  onViewBadges: () => void;
}

// Helper to determine correct level from XP
function getLevelFromXP(xp: number): CivicLevel {
  if (xp >= LEVEL_THRESHOLDS['Change-Maker'].min) return 'Change-Maker';
  if (xp >= LEVEL_THRESHOLDS['Catalyst'].min) return 'Catalyst';
  if (xp >= LEVEL_THRESHOLDS['Advocate'].min) return 'Advocate';
  if (xp >= LEVEL_THRESHOLDS['Sense-Maker'].min) return 'Sense-Maker';
  if (xp >= LEVEL_THRESHOLDS['Explorer'].min) return 'Explorer';
  return 'Observer';
}

export default function CivicLevelCard({
  civicLevel,
  xp,
  onViewBadges,
}: CivicLevelCardProps) {
  const [showMPInfo, setShowMPInfo] = useState(false);
  const [currentBadge, setCurrentBadge] = useState<Badge | null>(null);

  // Use calculated level from XP instead of database level (handles data inconsistencies)
  const actualLevel = getLevelFromXP(xp);
  const displayLevel = actualLevel;

  const progress = calculateLevelProgress(xp, displayLevel);
  const nextLevel = getNextLevel(displayLevel);

  const currentMP = progress.current; // XP within current level
  const nextLevelMP = progress.needed; // XP needed to reach next level

  useEffect(() => {
    async function loadBadge() {
      const badges = await getAllBadges();
      const badge = badges.find(b => b.level_name === displayLevel);
      if (badge) {
        setCurrentBadge(badge);
      }
    }
    loadBadge();
  }, [displayLevel]);


  const getLevelOneLiner = (level: CivicLevel): string => {
    const oneLiners: Record<CivicLevel, string> = {
      'Observer': "You're building awareness of the world around you.",
      'Explorer': "You're actively seeking out stories, patterns, and perspectives.",
      'Sense-Maker': "You're connecting the dots between issues, history, and lived experience.",
      'Advocate': "You're beginning to speak up and share what you've learned with others.",
      'Catalyst': "Your actions are influencing others and shaping collective energy.",
      'Change-Maker': "You're contributing meaningfully to civic outcomes and community impact.",
    };
    return oneLiners[level];
  };

  return (
    <>
      <div className="bg-[#181818] rounded-2xl p-6 border border-[#282828]" style={{ boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16), inset 0px 1px 1px rgba(255, 255, 255, 0.04), inset 0px -1px 2px rgba(0, 0, 0, 0.22)' }}>
        <div className="flex items-start justify-between mb-4">
          {/* CASING CONVENTION: Section titles should be in Title Case */}
          <h3 className="text-[14px] font-semibold text-[#F5F5F5]">
            Your Civic Level
          </h3>
          <button
            onClick={() => setShowMPInfo(true)}
            className="p-1 hover:bg-[#222222] rounded-full transition-colors"
            aria-label="Information about your civic level"
          >
            <Info className="w-4 h-4 text-[#A0A0A0]" />
          </button>
        </div>


        <div className="mb-4">
          <div className="flex items-center gap-3 mb-1.5">
            {currentBadge && (
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center text-xl flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${currentBadge.color_primary}, ${currentBadge.color_secondary})`,
                }}
              >
                {currentBadge.icon_symbol}
              </div>
            )}
            <h4 className="text-[26px] font-bold text-[#40E0D0]">
              {displayLevel}
            </h4>
          </div>
          <p className="text-[13px] text-[#CCCCCC]" style={{ lineHeight: '1.5' }}>
            {getLevelOneLiner(displayLevel)}
          </p>
        </div>

        {nextLevel && (
          <>
            <div className="mb-3">
              <p className="text-[12px] text-[#A0A0A0] mb-2">
                Progress to {nextLevel}
              </p>
            </div>

            <div
              className="h-2 bg-[#222222] rounded-full overflow-hidden mb-2"
              role="progressbar"
              aria-label={`Progress to ${nextLevel}: ${currentMP} out of ${nextLevelMP} Civic Points`}
              aria-valuenow={currentMP}
              aria-valuemin={0}
              aria-valuemax={nextLevelMP}
            >
              <div
                className="h-full bg-[#40E0D0] rounded-full transition-all duration-500"
                style={{ width: `${progress.percent}%` }}
              />
            </div>

            <p className="text-[12px] text-[#A0A0A0] mb-4">
              {currentMP} / {nextLevelMP} CP to {nextLevel}
            </p>
          </>
        )}

        {!nextLevel && (
          <div className="mb-4">
            <p className="text-[12px] text-[#A0A0A0]">
              Max level reached
            </p>
          </div>
        )}

        <button
          onClick={onViewBadges}
          className="text-[12px] text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors font-medium"
        >
          View your badges
        </button>
      </div>

      <CivicModal
        isOpen={showMPInfo}
        onClose={() => setShowMPInfo(false)}
        title="About your civic level"
        icon={Info}
        maxWidth="md"
      >
        <div
          className="bg-[#1A1A1A] rounded-xl border"
          style={{
            borderColor: 'rgba(255, 255, 255, 0.08)',
            padding: '32px 24px',
          }}
        >
          <div>
            <h3
              className="font-semibold"
              style={{
                fontSize: '16px',
                color: '#40E0D0',
                marginBottom: '8px',
                letterSpacing: '0.01em',
              }}
            >
              Your civic level
            </h3>
            <p
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
              }}
            >
              Your Civic Level reflects how your habits grow over time. From noticing, to understanding, to contributing, to shaping change.
            </p>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3
              className="font-semibold"
              style={{
                fontSize: '16px',
                color: '#40E0D0',
                marginBottom: '8px',
                letterSpacing: '0.01em',
              }}
            >
              How Civic Points works
            </h3>
            <p
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
              }}
            >
              You earn Civic Points (CP) by returning over time and actively engaging as you explore stories, reflect, and make sense of the world around you. CP isn't about speed or volume. It quietly tracks consistency.
            </p>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3
              className="font-semibold"
              style={{
                fontSize: '16px',
                color: '#40E0D0',
                marginBottom: '8px',
                letterSpacing: '0.01em',
              }}
            >
              How you grow
            </h3>
            <p
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
              }}
            >
              As CP builds, you unlock new levels that reflect your evolving civic practice. Small, repeated actions compound into meaningful progress.
            </p>
          </div>

          <div style={{ marginTop: '20px' }}>
            <h3
              className="font-semibold"
              style={{
                fontSize: '16px',
                color: '#40E0D0',
                marginBottom: '8px',
                letterSpacing: '0.01em',
              }}
            >
              Why it matters
            </h3>
            <p
              className="text-[#E0E0E0]"
              style={{
                fontSize: '14px',
                lineHeight: '1.65',
              }}
            >
              Your level helps you see the rhythm of your engagement. Showing how everyday attention turns into real momentum toward change.
            </p>
          </div>
        </div>
      </CivicModal>
    </>
  );
}
